"use client";

import Link from "next/link";

import { CheckCircle2, Sparkles } from "lucide-react";

import { Section } from "@/components/section";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

type LandingPackagesSectionProps = {
  contactLink: string;
  whatsappLink: string;
};

type LandingPackage = {
  title: string;
  price: string;
  frequency: string;
  badge: string;
  value: string;
  note?: string;
  features: string[];
  highlight?: boolean;
  ctaPrimary: { label: string; href: string };
  ctaSecondary: { label: string; href: string };
};

export function LandingPackagesSection({
  contactLink,
  whatsappLink,
}: LandingPackagesSectionProps) {
  const packages: LandingPackage[] = [
    {
      title: "Website Landing Page & Siap Dipakai",
      price: "Rp 350.000",
      frequency: "/ tahun",
      badge: "Starter",
      value:
        "Cocok buat yang ingin punya website cepat jadi untuk ngenalin produk, jasa, atau sekadar tes penawaran.",
      features: [
        "1 halaman website landing page",
        "Tampilan rapi dan nyaman dilihat",
        "Tombol WhatsApp langsung aktif",
        "Isi teks utama dibantu disiapkan",
        "Bisa dibuka dengan nyaman di HP dan laptop",
        "Website cepat dibuka",
        "Alamat website gratis (contoh: nama.vercel.app)",
        "Link ke sosial media",
        "Desain template (bukan custom)",
        "Revisi terbatas (2–4 kali)",
        "Website aktif selama 1 tahun",
      ],
      ctaPrimary: { label: "Pilih Paket", href: contactLink },
      ctaSecondary: { label: "Konsultasi", href: whatsappLink },
    },
    {
      title: "Website dengan Nama Brand Sendiri",
      price: "Rp 600.000",
      frequency: "/ tahun",
      badge: "Most Popular",
      value:
        "Cocok buat usaha yang ingin kelihatan lebih profesional dan mulai serius bangun kepercayaan lewat website.",
      note: "Basic Class: paling sering dipilih klien baru.",
      features: [
        "Website pakai nama domain sendiri (.com) aktif 1 tahun",
        "Website static yang ringan & cepat dibuka",
        "Tampilan custom, disesuaikan dengan usaha Anda",
        "Nyaman dibuka di HP maupun laptop",
        "Maksimal 3 halaman (Beranda, Layanan / Produk, Kontak / Profil Usaha)",
        "Susunan halaman rapi dan mudah dipahami pengunjung",
        "Tombol WhatsApp selalu tampil dan mudah dihubungi",
        "Link ke sosial media (Instagram, Facebook, dll)",
        "Dasar pengaturan agar website lebih mudah dikenali Google",
        "Revisi terbatas (4–8 kali)",
        "Website aktif dan bisa digunakan selama 1 tahun",
      ],
      ctaPrimary: { label: "Pilih Paket", href: contactLink },
      ctaSecondary: { label: "Konsultasi", href: whatsappLink },
    },
    {
      title: "Business Landing",
      price: "Rp 1.200.000",
      frequency: "/ tahun",
      badge: "Naik Kelas",
      value:
        "Landing page premium yang dirancang untuk menangkap data prospek lebih banyak.",
      note: "Bukan cuma tampil, tapi ngumpulin calon pelanggan.",
      highlight: true,
      features: [
        "Website pakai nama domain sendiri (.com) aktif 1 tahun",
        "Website hingga 5 halaman",
        "Desain premium dengan animasi halus yang bikin website terasa hidup",
        "Form kontak yang menyimpan data calon pelanggan secara otomatis ke Google Sheet",
        "Nyaman dibuka di HP maupun laptop",
        "Tombol WhatsApp dengan pesan otomatis siap kirim",
        "Halaman testimoni & konten pendukung kepercayaan",
        "Website lebih cepat dan stabil saat dibuka",
        "Tampilan rapi saat dibagikan ke WhatsApp atau sosial media",
        "Pengaturan halaman agar website lebih mudah dikenali Google",
        "Gratis 1 logo sederhana untuk kebutuhan bisnis",
        "Banner Call to Action untuk mengajak pengunjung menghubungi Anda",
        "Revisi lebih fleksibel sesuai kebutuhan",
        "Website aktif dan siap dipakai selama 1 tahun",
      ],
      ctaPrimary: { label: "Pilih Paket", href: contactLink },
      ctaSecondary: { label: "Konsultasi", href: whatsappLink },
    },
  ];

  return (
    <div className="relative isolate overflow-hidden bg-gradient-to-b from-[#f7fbff] via-white to-[#fef6ff]">
      <div className="pointer-events-none absolute inset-x-0 top-1/3 hidden h-64 w-full bg-[radial-gradient(circle_at_center,_rgba(8,131,149,0.22),_transparent_60%)] blur-[140px] sm:block" />
      <div className="pointer-events-none absolute left-1/4 top-10 hidden h-40 w-40 rounded-full bg-[#35A29F]/30 blur-[120px] sm:block" />
      <div className="pointer-events-none absolute bottom-0 right-6 hidden h-56 w-56 rounded-full bg-[#F2F7A1]/60 blur-[110px] sm:block" />
      <Section
        id="pricing"
        eyebrow="Paket Landing Page"
        title="Desain fokus konversi dengan pengalaman terbaik"
        description="Tiga opsi landing page untuk berbagai tahap bisnis. Tiap paket sudah termasuk copywriting utama, struktur CTA, dan tombol WhatsApp yang siap digunakan."
        align="center"
        className="relative z-10"
      >
        <div className="grid gap-6 lg:grid-cols-3">
          {packages.map((pkg) => (
            <article
              key={pkg.title}
              className={cn(
                "relative h-full rounded-[32px] transition duration-300 hover:-translate-y-1",
                pkg.highlight &&
                  "before:absolute before:inset-0 before:-z-10 before:rounded-[40px] before:bg-gradient-to-r before:from-[#8342FF]/30 before:via-[#35A29F]/25 before:to-[#088395]/25 before:blur-3xl",
              )}
            >
              <div
                className={cn(
                  "flex h-full flex-col rounded-[32px] border bg-white/90 p-8 shadow-[0_40px_120px_-80px_rgba(8,131,149,0.7)] backdrop-blur",
                  pkg.highlight
                    ? "border-[#8342FF]/40 shadow-[0_40px_120px_-60px_rgba(131,66,255,0.6)]"
                    : "border-[#071952]/10",
                )}
              >
                <div className="space-y-5">
                  <div className="flex items-center justify-between gap-3">
                    <Badge variant={pkg.highlight ? "success" : "outline"}>
                      {pkg.badge}
                    </Badge>
                    {pkg.highlight && (
                      <span className="flex items-center gap-1 rounded-full bg-[#F2F7A1]/80 px-3 py-1 text-xs font-semibold text-[#071952]">
                        <Sparkles className="h-3.5 w-3.5 text-[#8342FF]" />
                        Prioritas
                      </span>
                    )}
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold text-[#071952]">
                      {pkg.title}
                    </h3>
                    <p className="mt-2 text-base text-[#071952]/80">
                      {pkg.value}
                    </p>
                  </div>
                  {pkg.note && (
                    <p className="rounded-2xl bg-[#F2F7A1]/70 px-4 py-3 text-sm font-medium text-[#071952]">
                      {pkg.note}
                    </p>
                  )}
                  <div className="flex items-baseline gap-3">
                    <span className="text-4xl font-semibold text-[#071952]">
                      {pkg.price}
                    </span>
                    <span className="text-sm font-medium text-[#071952]/70">
                      {pkg.frequency}
                    </span>
                  </div>
                </div>

                <div className="mt-8 flex flex-1 flex-col">
                  <ul className="flex-1 space-y-3 text-sm text-[#071952]/85">
                    {pkg.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-start gap-3 rounded-2xl bg-[#F5FAFF] px-3 py-2"
                      >
                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#088395]" />
                        <span className="leading-relaxed">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-6 flex flex-col gap-3 pt-2 sm:flex-row">
                    <Button
                      asChild
                      className={cn(
                        "w-full rounded-2xl py-5 text-base",
                        pkg.highlight && "from-[#8342FF] via-[#35A29F] to-[#088395]",
                      )}
                    >
                      <Link href={pkg.ctaPrimary.href}>{pkg.ctaPrimary.label}</Link>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      className="w-full rounded-2xl py-5 text-base"
                    >
                      <Link href={pkg.ctaSecondary.href}>
                        {pkg.ctaSecondary.label}
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </Section>
    </div>
  );
}
